// ── DeckCardGrid ──────────────────────────────────────────────────────────────
// Handles three states: loading spinner, populated card grid, empty state.

import { Box, CircularProgress, Typography } from "@mui/material";
import React from "react";

import { PublishedCard } from "@/cards/common/types";

import { GRID_COLS } from "@/cards/common/constant/app-deck";
import type { DeckCardThumbnailRecord } from "@/cards/common/types/app-deck/deckCardThumbnail.types";
// import type { DeckCardLibraryExpandPortalProps } from "@/cards/common/component/deck-library/types";
import { DeckCardType } from "@/cards/common/types";
import DeckCardThumbnailTile from "./DeckCardThumbnailTile";
import DeckCardLibraryExpandPortal, { DeckCardLibraryExpandPortalProps } from "./DeckCardLibraryExpandPortal";

interface DeckCardGridProps {
  loading: boolean;
  cards: PublishedCard[];
  cardType: DeckCardType;
  darkMode: boolean;
  searchQuery: string;
  onCardClick: (card: PublishedCard) => void;
  thumbnailsByCardId?: Map<string, DeckCardThumbnailRecord>;
  expandPortalProps?: DeckCardLibraryExpandPortalProps;
}

const DeckCardGrid: React.FC<DeckCardGridProps> = ({
  loading,
  cards,
  cardType,
  darkMode,
  searchQuery,
  onCardClick,
  thumbnailsByCardId,
  expandPortalProps,
}) => {
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <CircularProgress
          sx={{
            color: darkMode ? "var(--neutralWhite)" : "var(--neutral-600)",
          }}
        />
      </Box>
    );
  }

  if (cards.length === 0) {
    return (
      <Box
        sx={{
          borderRadius: 3,
          border: "1px dashed",
          borderColor: darkMode ? "var(--neutral-700)" : "var(--neutral-300)",
          backgroundColor: darkMode ? "var(--neutral-900)" : "white",
          px: { xs: 2, sm: 4 },
          py: { xs: 4, sm: 6 },
          textAlign: "center",
          maxWidth: 520,
          mx: "auto",
          mt: 4,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontFamily: '"Lexend", sans-serif',
            fontWeight: 600,
            color: darkMode ? "var(--neutral-100)" : "var(--neutral-900)",
            mb: 1,
          }}
        >
          No cards available
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: darkMode ? "var(--neutral-400)" : "var(--neutral-600)" }}
        >
          {searchQuery
            ? "No cards match your search."
            : "Check back later — new cards will appear here once published."}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {expandPortalProps && (
        <DeckCardLibraryExpandPortal {...expandPortalProps} />
      )}
      <Box
        sx={{
          display: "grid",
          gap: { xs: 1.5, sm: 2, md: 3 },
          gridTemplateColumns: GRID_COLS[cardType],
          justifyContent: "flex-start",
        }}
      >
        {cards.map((card) => (
          <Box
            key={card.id}
            sx={{
              width: "100%",
              minWidth: 0,
              display: "flex",
              justifyContent: "stretch",
            }}
          >
            <DeckCardThumbnailTile
              card={card}
              thumbnail={thumbnailsByCardId?.get(card.id) ?? null}
              isThumbnailLoading={loading}
              onClick={() => onCardClick(card)}
            />
          </Box>
        ))}
      </Box>
    </>
  );
};

export default DeckCardGrid;
